import {
  Alert,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
  createTheme,
  defaultVariantColorsResolver,
} from '@mantine/core';

import { primitiveScales } from '../tokens/generated';
import { chromaticTones, dsRoles, isChromaticTone, isDsColor, lightHover } from './roles';

const MANTINE_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
const MANTINE_DARK_SHADES = [100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

function tuple(scale: string, shades = MANTINE_SHADES): MantineColorsTuple {
  const values = primitiveScales[scale];
  if (!values) throw new Error(`Палитры ${scale} нет в токенах ui-kit`);
  return shades.map((shade) => values[shade]) as unknown as MantineColorsTuple;
}

const primitivePalettes = Object.fromEntries(
  Object.entries(primitiveScales)
    .filter(([, shades]) => MANTINE_SHADES.every((shade) => shade in shades))
    .map(([name]) => [name, tuple(name)]),
);

const rolePalettes = Object.fromEntries(Object.entries(dsRoles).map(([name, role]) => [name, tuple(role.palette)]));

const alertFlatColors: Record<string, { background: string; color: string }> = {
  accent: { background: 'var(--color-surface-2)', color: 'var(--color-foreground-foreground)' },
  action: { background: 'var(--color-chromatic-soft-blue-background)', color: 'var(--color-action-default)' },
  success: { background: 'var(--color-chromatic-soft-green-background)', color: 'var(--color-success-default)' },
  warning: { background: 'var(--color-chromatic-orange-background)', color: 'var(--color-warning-default)' },
  danger: { background: 'var(--color-chromatic-red-background)', color: 'var(--color-danger-default)' },
};

const variantColorResolver: VariantColorsResolver = (input) => {
  const color = input.color ?? input.theme.primaryColor;

  if (isDsColor(color)) {
    const role = dsRoles[color];
    switch (input.variant) {
      case 'filled':
        return {
          background: role.filled,
          hover: role.filledHover,
          color: role.contrast,
          hoverColor: role.contrast,
          border: '1px solid transparent',
        };
      case 'light':
        return {
          background: role.light,
          hover: lightHover(role.light),
          color: role.lightColor,
          hoverColor: role.lightColor,
          border: '1px solid transparent',
        };
      case 'default':
        return {
          background: 'var(--color-default-default)',
          hover: 'var(--color-default-hover)',
          color: role.text,
          hoverColor: role.text,
          border: '1px solid var(--color-border-default)',
        };
      case 'subtle':
        return {
          background: 'transparent',
          hover: 'var(--color-default-default)',
          color: role.text,
          hoverColor: role.text,
          border: '1px solid transparent',
        };
      case 'outline':
        return {
          background: 'transparent',
          hover: 'var(--color-default-hover)',
          color: role.text,
          hoverColor: role.text,
          border: '1px solid var(--color-border-default)',
        };
      case 'transparent':
        return { background: 'transparent', hover: 'transparent', color: role.text, border: 'none' };
    }
  }

  if (isChromaticTone(color) && input.variant === 'light') {
    const tone = chromaticTones[color];
    return {
      background: `var(--color-chromatic-${tone}-background)`,
      hover: lightHover(`var(--color-chromatic-${tone}-background)`),
      color: `var(--color-chromatic-${tone}-foreground)`,
      border: '1px solid transparent',
    };
  }

  return defaultVariantColorsResolver(input);
};

/** Палитры ui-kit целиком, роли ДС и dark для тёмной схемы Mantine; проп color типом сужен до ролей и тонов (mantine.d.ts). */
const colors: Record<string, MantineColorsTuple> = {
  ...primitivePalettes,
  ...rolePalettes,
  gray: tuple('neutral'),
  dark: tuple('neutral', MANTINE_DARK_SHADES),
};

export const theme = createTheme({
  colors,
  primaryColor: 'accent',
  primaryShade: { light: 9, dark: 0 },
  variantColorResolver,
  activeClassName: '',
  cursorType: 'pointer',
  scale: 1,
  fontSmoothing: true,
  white: '#ffffff',
  black: '#1b1b1b',

  fontFamily: 'var(--text-font-family)',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSizes: {
    xs: 'var(--body-xs-font-size)',
    sm: 'var(--body-sm-font-size)',
    md: 'var(--body-base-font-size)',
    lg: 'var(--heading-3-font-size)',
    xl: 'var(--heading-2-font-size)',
  },
  lineHeights: {
    xs: 'var(--body-xs-line-height)',
    sm: 'var(--body-sm-line-height)',
    md: 'var(--body-base-line-height)',
    lg: 'var(--heading-3-line-height)',
    xl: 'var(--heading-2-line-height)',
  },
  fontWeights: { regular: '400', medium: '500', bold: '600' },
  headings: {
    fontFamily: 'var(--text-font-family)',
    fontWeight: '600',
    sizes: {
      h1: {
        fontSize: 'var(--heading-1-font-size)',
        lineHeight: 'var(--heading-1-line-height)',
        fontWeight: 'var(--heading-1-font-weight)',
      },
      h2: {
        fontSize: 'var(--heading-2-font-size)',
        lineHeight: 'var(--heading-2-line-height)',
        fontWeight: 'var(--heading-2-font-weight)',
      },
      h3: {
        fontSize: 'var(--heading-3-font-size)',
        lineHeight: 'var(--heading-3-line-height)',
        fontWeight: 'var(--heading-3-font-weight)',
      },
      h4: {
        fontSize: 'var(--heading-4-font-size)',
        lineHeight: 'var(--heading-4-line-height)',
        fontWeight: 'var(--heading-4-font-weight)',
      },
      h5: {
        fontSize: 'var(--heading-5-font-size)',
        lineHeight: 'var(--heading-5-line-height)',
        fontWeight: 'var(--heading-5-font-weight)',
      },
      h6: {
        fontSize: 'var(--body-xs-medium-font-size)',
        lineHeight: 'var(--body-xs-medium-line-height)',
        fontWeight: 'var(--body-xs-medium-font-weight)',
      },
    },
  },

  radius: {
    xs: 'var(--radius-rounded-sm)',
    sm: 'var(--radius-sm)',
    md: 'var(--radius-md)',
    lg: 'var(--radius-lg)',
    xl: 'var(--radius-rounded-3xl)',
  },
  defaultRadius: 'md',
  spacing: {
    xs: 'var(--spacing-2)',
    sm: 'var(--spacing-3)',
    md: 'var(--spacing-4)',
    lg: 'var(--spacing-6)',
    xl: 'var(--spacing-8)',
  },
  shadows: {
    xs: 'var(--shadow-sm)',
    sm: 'var(--shadow-menu)',
    md: 'var(--shadow-md)',
    lg: 'var(--shadow-lg)',
    xl: 'var(--shadow-panel)',
  },
  breakpoints: { xs: '30em', sm: '40em', md: '48em', lg: '64em', xl: '80em' },

  components: {
    Button: { defaultProps: { size: 'md' } },
    ActionIcon: { defaultProps: { size: 'md', variant: 'subtle' } },
    CloseButton: { defaultProps: { size: 'md' } },
    TextInput: { defaultProps: { size: 'md' } },
    PasswordInput: { defaultProps: { size: 'md' } },
    NumberInput: { defaultProps: { size: 'md' } },
    Textarea: { defaultProps: { size: 'md' } },
    Select: { defaultProps: { size: 'md' } },
    MultiSelect: { defaultProps: { size: 'md' } },
    Autocomplete: { defaultProps: { size: 'md' } },
    TagsInput: { defaultProps: { size: 'md' } },
    Checkbox: { defaultProps: { size: 'sm', iconColor: 'var(--color-accent-foreground)' } },
    Radio: { defaultProps: { size: 'sm', iconColor: 'var(--color-accent-foreground)' } },
    Switch: { defaultProps: { size: 'sm', withThumbIndicator: false, radius: 'var(--radius-rounded-full)' } },
    Badge: { defaultProps: { size: 'md', variant: 'light', color: 'gray', radius: 'var(--radius-rounded-full)' } },
    Avatar: { defaultProps: { size: 'md', radius: 'var(--radius-rounded-full)' } },
    Tooltip: { defaultProps: { radius: 'md', openDelay: 200 } },
    Popover: { defaultProps: { radius: 'lg', shadow: 'xl' } },
    Menu: { defaultProps: { radius: 'lg', shadow: 'sm' } },
    Combobox: { defaultProps: { radius: 'lg', shadow: 'xl' } },
    Modal: { defaultProps: { radius: 'lg', size: 'md', centered: true } },
    Drawer: { defaultProps: { radius: 'lg' } },
    Card: { defaultProps: { radius: 'lg', padding: 'md' } },
    Paper: { defaultProps: { radius: 'md' } },
    Notification: { defaultProps: { radius: 'lg' } },
    Alert: Alert.extend({
      defaultProps: { radius: 'lg', variant: 'light', color: 'action' },
      vars: (_theme, props) => {
        const flat = props.variant === 'light' ? alertFlatColors[props.color ?? 'action'] : undefined;
        return flat ? { root: { '--alert-bg': flat.background, '--alert-color': flat.color } } : { root: {} };
      },
    }),
    SegmentedControl: { defaultProps: { radius: 'md', size: 'sm', withItemsBorders: false } },
    Progress: { defaultProps: { size: 'md', radius: 'var(--radius-rounded-full)', color: 'accent' } },
    Loader: { defaultProps: { color: 'accent', size: 'sm' } },
    Pagination: { defaultProps: { size: 'md', radius: 'md' } },
    Chip: { defaultProps: { size: 'md', radius: 'md' } },
    Skeleton: { defaultProps: { radius: 'md' } },
    NavLink: { defaultProps: { variant: 'subtle' } },
    Table: { defaultProps: { horizontalSpacing: 20, verticalSpacing: 10, withTableBorder: true, highlightOnHover: true } },
    ScrollArea: { defaultProps: { scrollbarSize: 8 } },
  },
});

const schemeVariables: Record<string, string> = {
  '--mantine-color-body': 'var(--color-background-default)',
  '--mantine-color-text': 'var(--color-foreground-foreground)',
  '--mantine-color-bright': 'var(--color-foreground-foreground)',
  '--mantine-color-dimmed': 'var(--color-foreground-muted-default)',
  '--mantine-color-placeholder': 'var(--color-foreground-muted-default)',
  '--mantine-color-anchor': 'var(--color-action-default)',
  '--mantine-color-default': 'var(--color-default-default)',
  '--mantine-color-default-hover': 'var(--color-default-hover)',
  '--mantine-color-default-color': 'var(--color-default-foreground)',
  '--mantine-color-default-border': 'var(--color-border-default)',
  '--mantine-color-error': 'var(--color-danger-default)',
  '--mantine-color-success': 'var(--color-success-default)',
  '--mantine-color-disabled': 'var(--color-default-default)',
  '--mantine-color-disabled-color': 'var(--color-foreground-muted-default)',
  '--mantine-color-disabled-border': 'var(--color-border-default)',
  '--mantine-primary-color-filled': dsRoles.accent.filled,
  '--mantine-primary-color-filled-hover': dsRoles.accent.filledHover,
  '--mantine-primary-color-light': dsRoles.accent.light,
  '--mantine-primary-color-light-hover': lightHover(dsRoles.accent.light),
  '--mantine-primary-color-light-color': dsRoles.accent.lightColor,
  '--mantine-primary-color-contrast': dsRoles.accent.contrast,
};

for (const [name, role] of Object.entries(dsRoles)) {
  Object.assign(schemeVariables, {
    [`--mantine-color-${name}-filled`]: role.filled,
    [`--mantine-color-${name}-filled-hover`]: role.filledHover,
    [`--mantine-color-${name}-light`]: role.light,
    [`--mantine-color-${name}-light-hover`]: lightHover(role.light),
    [`--mantine-color-${name}-light-color`]: role.lightColor,
    [`--mantine-color-${name}-outline`]: role.text,
    [`--mantine-color-${name}-outline-hover`]: 'var(--color-default-hover)',
    [`--mantine-color-${name}-text`]: role.text,
  });
}

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: schemeVariables,
  dark: schemeVariables,
});
